/**
 * Cart Action Logging Utility
 *
 * Provides structured logging for cart-related user actions
 * to help with debugging, analytics, and user behavior tracking.
 */

import appSettings from '../config/settings';
import { types as sdkTypes } from './sdkLoader';
import { convertMoneyToNumber } from './currency';
import { trackRedditAddToCart } from './redditPixelEvents';
import { trackMetaAddToCart } from './metaPixelEvents';

const { Money } = sdkTypes;

/**
 * Log levels for different types of cart events
 */
export const LOG_LEVELS = {
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
  DEBUG: 'debug',
};

/**
 * Cart action types for categorization
 */
export const CART_ACTIONS = {
  ADD_TO_CART: 'add_to_cart',
  REMOVE_FROM_CART: 'remove_from_cart',
  UPDATE_QUANTITY: 'update_quantity',
  CART_VIEW: 'cart_view',
  CHECKOUT_INITIATED: 'checkout_initiated',
  CART_CLEARED: 'cart_cleared',
};

/**
 * Enhanced logging function for cart actions
 * @param {string} action - The cart action being performed
 * @param {Object} data - Additional data about the action
 * @param {string} level - Log level (info, warn, error, debug)
 */
export const logCartAction = (action, data = {}, level = LOG_LEVELS.INFO) => {
  const timestamp = new Date().toISOString();
  const logData = {
    timestamp,
    action,
    level,
    data: {
      ...data,
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'server',
      url: typeof window !== 'undefined' ? window.location.href : 'server',
      environment: appSettings.env,
    },
  };

  // Send to external analytics if configured
  if (typeof window !== 'undefined' && window.gtag) {
    try {
      window.gtag('event', 'cart_action', {
        event_category: 'ecommerce',
        event_label: action,
        custom_map: {
          cart_action: action,
          cart_data: JSON.stringify(data),
        },
      });
    } catch (error) {
      // Silently fail analytics
    }
  }

  return logData;
};

/**
 * Get emoji for cart action type
 * @param {string} action - The cart action
 * @returns {string} Emoji representation
 */
const getActionEmoji = action => {
  const emojiMap = {
    [CART_ACTIONS.ADD_TO_CART]: '🛒',
    [CART_ACTIONS.REMOVE_FROM_CART]: '🗑️',
    [CART_ACTIONS.UPDATE_QUANTITY]: '📊',
    [CART_ACTIONS.CART_VIEW]: '👀',
    [CART_ACTIONS.CHECKOUT_INITIATED]: '💳',
    [CART_ACTIONS.CART_CLEARED]: '🧹',
  };
  return emojiMap[action] || '📦';
};

/**
 * Log Add to Cart action with detailed product information
 * @param {Object} listing - The product listing being added
 * @param {Object} orderData - Order data (quantity, delivery method, etc.)
 * @param {Object} additionalData - Any additional context data
 */
export const logAddToCart = (listing, orderData = {}, additionalData = {}) => {
  const listingData = {
    listingId: listing?.id?.uuid || listing?.id,
    title: listing?.attributes?.title,
    price: listing?.attributes?.price,
    currency: listing?.attributes?.currency,
    authorId: listing?.author?.id?.uuid || listing?.author?.id,
    authorName: listing?.author?.attributes?.profile?.displayName,
    currentStock: listing?.currentStock?.attributes?.quantity,
    category: listing?.attributes?.publicData?.categoryLevel1,
    brand: listing?.attributes?.publicData?.brand,
  };

  const orderDataClean = {
    quantity: orderData?.quantity,
    deliveryMethod: orderData?.deliveryMethod,
    priceVariantName: orderData?.priceVariantName,
  };

  const logPayload = logCartAction(CART_ACTIONS.ADD_TO_CART, {
    listing: listingData,
    orderData: orderDataClean,
    ...additionalData,
  });

  try {
    const price = listing?.attributes?.price;
    const qty = Number(orderData?.quantity) || 1;
    const contentId = listing?.id?.uuid || listing?.id;
    if (price instanceof Money && price.currency) {
      const unitMajor = convertMoneyToNumber(price);
      trackRedditAddToCart({
        value: unitMajor * qty,
        currency: price.currency,
        itemCount: qty,
      });
      trackMetaAddToCart({
        contentId,
        value: unitMajor * qty,
        currency: price.currency,
        quantity: qty,
      });
    } else {
      trackRedditAddToCart();
      trackMetaAddToCart({ contentId, quantity: qty });
    }
  } catch (_) {
    // Silently fail pixel analytics
  }

  return logPayload;
};

/**
 * Log cart quantity update action
 * @param {string} listingId - The listing ID being updated
 * @param {number} oldQuantity - Previous quantity
 * @param {number} newQuantity - New quantity
 * @param {Object} additionalData - Any additional context data
 */
export const logUpdateQuantity = (listingId, oldQuantity, newQuantity, additionalData = {}) => {
  return logCartAction(CART_ACTIONS.UPDATE_QUANTITY, {
    listingId,
    oldQuantity,
    newQuantity,
    quantityChange: newQuantity - oldQuantity,
    ...additionalData,
  });
};

/**
 * Log cart view action
 * @param {Object} cartData - Current cart state
 * @param {Object} additionalData - Any additional context data
 */
export const logCartView = (cartData = {}, additionalData = {}) => {
  const cartSummary = {
    totalItems: Object.values(cartData).reduce((total, items) => total + items.length, 0),
    providers: Object.keys(cartData).length,
    items: Object.entries(cartData).map(([providerId, items]) => ({
      providerId,
      itemCount: items.length,
      items: items.map(item => ({
        listingId: item.listingId,
        quantity: item.quantity,
        deliveryMethod: item.deliveryMethod,
      })),
    })),
  };

  return logCartAction(CART_ACTIONS.CART_VIEW, {
    cartSummary,
    ...additionalData,
  });
};

/**
 * Log cart errors with context
 * @param {string} action - The action that failed
 * @param {Error} error - The error that occurred
 * @param {Object} context - Additional context about the failure
 */
export const logCartError = (action, error, context = {}) => {
  return logCartAction(
    action,
    {
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name,
      },
      context,
    },
    LOG_LEVELS.ERROR
  );
};

/**
 * Log cart success actions
 * @param {string} action - The action that succeeded
 * @param {Object} result - The result data
 * @param {Object} additionalData - Any additional context data
 */
export const logCartSuccess = (action, result = {}, additionalData = {}) => {
  return logCartAction(
    action,
    {
      result,
      ...additionalData,
    },
    LOG_LEVELS.INFO
  );
};
